import { useEffect, useState } from "react";
import { Flame, ArrowUpRight, X, Sparkles } from "lucide-react";
import useGClubStatus from "@/hooks/useGClubStatus";
import useCountdown from "@/hooks/useCountdown";

const RIBBON_DISMISS_KEY = "gclub_ribbon_dismissed_this_session";

const track = (event, extra = {}) => {
  try {
    if (typeof window !== "undefined" && window.posthog) {
      window.posthog.capture(event, extra);
    }
  } catch {
    /* silent */
  }
};

export const GenesisClubRibbon = ({ onOpenModal, onVisibilityChange }) => {
  const { status } = useGClubStatus();
  const [dismissed, setDismissed] = useState(false);
  const countdown = useCountdown(status?.deadline_iso);

  useEffect(() => {
    try {
      setDismissed(!!sessionStorage.getItem(RIBBON_DISMISS_KEY));
    } catch {
      /* silent */
    }
  }, []);

  const timeUp = !!status && countdown.expired;
  const visible = !!status && !status.closed && !timeUp && !dismissed;

  useEffect(() => {
    onVisibilityChange?.(visible);
  }, [visible, onVisibilityChange]);

  if (!visible) return null;

  const urgent = status.remaining <= 5;

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      sessionStorage.setItem(RIBBON_DISMISS_KEY, "1");
    } catch {
      /* silent */
    }
    setDismissed(true);
    track("gclub_ribbon_dismissed", { remaining: status.remaining });
  };

  const handleOpen = (e) => {
    // Whole-ribbon click now goes DIRECTLY to Shopify (CTA pill was removed).
    // The X dismiss button still stops propagation so it doesn't fire this.
    track("gclub_ribbon_clicked", { remaining: status.remaining });
    // Fallback in case anchor navigation is blocked
    if (!e?.currentTarget?.href) {
      window.open(status.product_url, "_blank", "noreferrer");
    }
  };

  // Urgent state stays dark red for maximum alarm.
  // Non-urgent state is the bright cream/white with animated gold shimmer.
  return (
    <a
      data-testid="gclub-ribbon"
      href={status.product_url}
      target="_blank"
      rel="noreferrer"
      onClick={handleOpen}
      className={`fixed top-0 inset-x-0 z-[60] block select-none overflow-hidden no-underline ${
        urgent
          ? "gclub-ribbon-urgent-pulse border-b border-[rgba(255,90,60,0.6)]"
          : "gclub-ribbon-pulse gclub-ribbon-shimmer border-b border-[rgba(255,208,102,0.55)]"
      }`}
    >
      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-5 flex items-center gap-4 md:gap-6 z-10">
        {/* Left icon */}
        <span
          className={`inline-flex shrink-0 items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-full ${
            urgent
              ? "bg-[rgba(255,90,60,0.28)]"
              : "bg-[#e11d1d] shadow-[0_0_18px_rgba(225,29,29,0.6)]"
          }`}
        >
          <Flame
            size={22}
            className={urgent ? "text-[#ff5a3c] animate-pulse" : "text-white animate-pulse"}
            strokeWidth={2.8}
          />
        </span>

        {/* Main headline — SHYFT stays uppercase, rest is title case */}
        <span
          className={`font-black tracking-tight text-[17px] md:text-[22px] leading-tight ${
            urgent ? "text-[#ff5a3c]" : "text-[#c8102e]"
          }`}
          style={
            urgent
              ? undefined
              : { textShadow: "0 1px 0 rgba(255,255,255,0.5)" }
          }
        >
          Get Your <span className="uppercase tracking-wide">SHYFT</span>{" "}
          Genesis Club Membership Now
        </span>

        {/* Vertical separator — visual break between the two thoughts */}
        <span
          aria-hidden
          className={`hidden md:block w-px h-7 shrink-0 ${
            urgent ? "bg-[#ffb597]/40" : "bg-[#c8102e]/35"
          }`}
        />

        {/* Secondary: live countdown DD : HH : MM : SS */}
        <span
          data-testid="gclub-ribbon-countdown"
          className={`hidden md:inline-flex items-center gap-2 shrink-0 font-black tracking-tight tabular-nums text-[13px] md:text-[15px] uppercase ${
            urgent
              ? "text-white gclub-countdown-pulse-urgent"
              : "text-[#1a140d] gclub-countdown-pulse"
          }`}
          aria-label={`Time left: ${countdown.dd} days ${countdown.hh} hours ${countdown.mm} minutes ${countdown.ss} seconds`}
        >
          <span className={`text-[10px] md:text-[11px] font-bold tracking-[0.22em] ${urgent ? "text-[#ffb597]" : "text-[#c8102e]"}`}>
            Ends In
          </span>
          <span className="tabular-nums">
            {countdown.dd}
            <span className={urgent ? "text-[#ffb597] mx-0.5" : "text-[#c8102e] mx-0.5"}>:</span>
            {countdown.hh}
            <span className={urgent ? "text-[#ffb597] mx-0.5" : "text-[#c8102e] mx-0.5"}>:</span>
            {countdown.mm}
            <span className={urgent ? "text-[#ffb597] mx-0.5" : "text-[#c8102e] mx-0.5"}>:</span>
            {countdown.ss}
          </span>
        </span>

        {/* Right-side group: Claim Yours pill + Dismiss */}
        <div className="ml-auto shrink-0 flex items-center gap-3 md:gap-4">
          <span
            data-testid="gclub-ribbon-cta"
            className={`inline-flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 rounded-full font-black text-[13px] md:text-[15px] tracking-wide uppercase transition ${
              urgent
                ? "bg-white text-[#3a0d05] group-hover:bg-[#fff5f2]"
                : "bg-[#1a140d] text-[#ffcc4d] border border-[#ffcc4d]/60 group-hover:bg-black shadow-[0_4px_16px_rgba(0,0,0,0.35)]"
            }`}
          >
            <Sparkles size={16} strokeWidth={2.8} />
            <span>Claim Yours</span>
            <ArrowUpRight size={17} strokeWidth={2.8} />
          </span>

          <button
            type="button"
            onClick={handleDismiss}
            data-testid="gclub-ribbon-dismiss"
            aria-label="Dismiss for this session"
            className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition ${
              urgent
                ? "text-[#ffb597] hover:text-white hover:bg-black/30"
                : "text-[#c8102e] hover:text-white hover:bg-[#c8102e]"
            }`}
          >
            <X size={16} strokeWidth={2.6} />
          </button>
        </div>
      </div>
    </a>
  );
};

/** Tiny floating chip that reappears after the ribbon is dismissed, so
 *  the reminder stays visible without covering content. */
export const GenesisClubChip = ({ onOpenModal }) => {
  const { status } = useGClubStatus();
  const [ribbonDismissed, setRibbonDismissed] = useState(false);

  useEffect(() => {
    const check = () => {
      try {
        setRibbonDismissed(!!sessionStorage.getItem(RIBBON_DISMISS_KEY));
      } catch {
        /* silent */
      }
    };
    check();
    const id = window.setInterval(check, 1000);
    return () => window.clearInterval(id);
  }, []);

  if (!status || status.closed || !ribbonDismissed) return null;
  const urgent = status.remaining <= 5;

  return (
    <button
      type="button"
      onClick={() => {
        track("gclub_chip_clicked", { remaining: status.remaining });
        onOpenModal?.();
      }}
      data-testid="gclub-chip"
      className={`fixed z-[55] bottom-5 right-5 md:bottom-6 md:right-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-full backdrop-blur border shadow-[0_10px_30px_-8px_rgba(0,0,0,0.7)] transition hover:-translate-y-0.5 group ${
        urgent
          ? "bg-[#1a0a08]/85 border-[rgba(255,90,60,0.55)] product-breathe"
          : "bg-[#14110e]/85 border-[rgba(255,208,102,0.55)] product-breathe"
      }`}
    >
      <span
        className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
          urgent ? "bg-[rgba(255,90,60,0.18)]" : "bg-[rgba(255,208,102,0.22)]"
        }`}
      >
        <Flame
          size={13}
          className={urgent ? "text-[#ff5a3c] animate-pulse" : "text-[#ffcc4d]"}
        />
      </span>
      <span className="text-white text-[12px] font-semibold tracking-wide">
        {status.remaining} G Club Memberships Left
      </span>
      <ArrowUpRight
        size={13}
        strokeWidth={2.6}
        className="text-[#ffcc4d] transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
      />
    </button>
  );
};

export default GenesisClubRibbon;
